<template>
  <template v-for="column in columns" :key="column.name">
    <CellText
      v-if="column.key === 'actualValue'"
      :value="originalBalanceNumber"
      :data-testid="`row-${index}-${column.key}`"
    />
    <CellText
      v-else-if="column.key === 'adjustedValue'"
      :value="adjustedValue"
      :data-testid="`row-${index}-${column.key}`"
    />
    <CellInput
      v-else-if="column.type === 'text' || column.type === 'number'"
      :id="modelValue.id"
      :key="column.name"
      v-model="value[column.key]"
      :align="column.align"
      :disabled="isLocked"
      :focused="focusedColumn === column.key ? focusedAt : 0"
      :error="errors[column.key]"
      :required="column.required"
      :type="column.type"
      :value-key="column.key"
      :data-testid="`row-${index}-${column.key}`"
    />
    <CellSelect
      v-else-if="column.type === 'select'"
      :key="column.key"
      v-model="value[column.key]"
      :name="column.key"
      :options="column.options"
      :data-testid="`row-${index}-${column.key}`"
    />
    <CellActions
      v-else-if="column.key === 'actions'"
      v-show="showActions"
      :data-testid="`row-${index}-${column.key}`"
      :disabled="isLocked"
      @delete="handleDelete"
    />
    <div
      v-else-if="column.key === 'account'"
      class="tw-pl-6 tw-my-auto tw-max-h-[30px]"
      :data-testid="`row-${index}-${column.key}`"
    >
      <CellAccount
        v-model="value[column.key]"
        :disabled="isLocked"
        :focused="focusedColumn === 'account' ? focusedAt : 0"
        :error="errors[column.key]"
        @filled="focusColumn('description')"
        @focused="focusColumn('account')"
        @tab-forward="focusColumn('description')"
      />
    </div>
  </template>
  <div
    v-if="hasDivider"
    class="tw-bg-sf-neutral-lighter-2 tw-my-2 tw-col-span-full tw-h-[1px]"
  ></div>
</template>

<script setup lang="ts">
import type { FormError, SubAdjustmentRow } from '../types'

import { useVModel } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'

import CellAccount from './adjustments-account-selector.vue'
import CellActions from './adjustments-modal-row-cell-actions.vue'
import CellInput from './adjustments-modal-row-cell-input.vue'
import CellSelect from './adjustments-modal-row-cell-select.vue'
import CellText from './adjustments-modal-row-cell-text.vue'

import { useAdjustmentsModalStore } from '@app/application/adjustments/store/adjustment-modal'

interface Props {
  errors?: FormError
  focusedColumn?: string | null
  focusedAt?: number
  index?: number
  isDescriptionPristine?: boolean
  hasDivider?: boolean
  modelValue?: SubAdjustmentRow
  showActions?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  focusedColumn: null,
  focusedAt: 0,
  errors: () => ({} as FormError),
  index: 0,
  modelValue: () => ({} as SubAdjustmentRow),
})

const emit =
  defineEmits<{
    delete: [id: string | number]
    'update:modelValue': [value: SubAdjustmentRow]
  }>()

const value = useVModel(props, 'modelValue', emit, {
  passive: true,
  deep: true,
})

const modalStore = useAdjustmentsModalStore()

const {
  columns,
  emptySubAdjustmentRows,
  isLocked,
  originalAccountBalances,
  parsedValuesbyAdjustmentId,
  subAdjustmentIdsByAccountId,
} = storeToRefs(modalStore)

const originalBalanceNumber = computed(() => {
  const accountId = value.value.account?.id

  if (!accountId) {
    return 0
  }

  const actualValue = value.value.account?.actualValue
  const balance = originalAccountBalances.value[accountId]

  // If the account is part of another persisted sub adjustment already, display that balance for consistency
  if (balance || balance === 0) {
    return balance
  }

  return actualValue || 0
})

// Take into account other subadjustment rows that are already associated with the account adjustment so that the values update in sync
const debitNumber = computed(() => {
  if (!props.modelValue.account) {
    return 0
  }

  const accountId = props.modelValue?.account?.id

  if (!accountId) {
    // It is a new account, so we don't have to worry about other subadjustments
    return parsedValuesbyAdjustmentId.value[props.modelValue.id].valueDebit
  }

  return subAdjustmentIdsByAccountId.value[accountId].reduce((acc, subId) => {
    return acc + parsedValuesbyAdjustmentId.value[subId].valueDebit
  }, 0)
})

// Take into account other subadjustment rows that are already associated with the account in the adjustment so that the values update in sync
const creditNumber = computed(() => {
  if (!props.modelValue.account) {
    return 0
  }

  const accountId = props.modelValue?.account?.id

  if (!accountId) {
    // It is a new account, so we don't have to worry about other subadjustments
    return parsedValuesbyAdjustmentId.value[props.modelValue.id].valueCredit
  }

  return subAdjustmentIdsByAccountId.value[accountId].reduce((acc, subId) => {
    return acc + parsedValuesbyAdjustmentId.value[subId].valueCredit
  }, 0)
})

const adjustedValue = computed(
  () => originalBalanceNumber.value + debitNumber.value - creditNumber.value
)

function focusColumn(column: 'account' | 'description') {
  modalStore.focusColumn({
    id: value.value.id.toString(),
    column,
  })
}

function handleDelete() {
  emit('delete', value.value.id)
}

// Try to prefill the description of a legitimate row if empty on any update
// Don't prefill if the user has performed any action on the description field
watch(
  value,
  (newValue) => {
    if (
      !emptySubAdjustmentRows.value[value.value.id] &&
      !newValue.description &&
      props.isDescriptionPristine
    ) {
      modalStore.prefillAdjustment(value.value.id.toString())
    }
  },
  {
    deep: true,
    immediate: true,
  }
)
</script>
